import { TranslationKey } from '../../i18n/translationKeys';
import type { CompanyMatchStatus } from './companyMatch';

export interface BridgeSyncPreflightState {
    /** The opened dataset is behind the workspace's accepted position (backend /connect/desktop-auth). */
    isStaleCopy: boolean;
    /** Match between the launched société key and the company the bridge reports. */
    companyMatchStatus: CompanyMatchStatus;
}

/**
 * Reasons a bridge sync can't produce a correct result and must not run. Returns the localized message
 * to show the user, or null when sync may proceed.
 *
 * - Stale copy: the opened copy sits below the workspace high-water mark. The reason inverted when Acomba
 *   went full-sync-only (BR-19): it is no longer that an incremental read would send *nothing*, but that a
 *   Full sync would send *everything* and make the older data the accepted truth, silently discarding
 *   what is newer. That is the unrecoverable direction, which is why a Full sync is not an escape hatch
 *   here. The user promotes the copy (the banner's action) or opens the current one first.
 * - Company mismatch: the bridge reports a different company than the one launched; syncing would push
 *   a foreign dataset and corrupt relances. 'unverifiable' (manual setup, no key to compare) is allowed
 *   through — we can't confirm but also can't block.
 */
export function bridgeSyncBlockReason(
    state: BridgeSyncPreflightState,
    t: (key: string) => string,
): string | null {
    if (state.isStaleCopy) return t(TranslationKey.AcombaBridge.Errors.StaleCopySync);
    if (state.companyMatchStatus === 'mismatch') {
        return t(TranslationKey.AcombaBridge.CompanyMatch.MismatchWarning);
    }
    return null;
}
